import React, { useState } from 'react';
import AudioRecorder from './AudioRecorder';
import RecordingAudioPlayback, { RecordingRetranscriptionAction } from './RecordingAudioPlayback';
import BrowserLocalRecordingPlayback from './BrowserLocalRecordingPlayback';
import { Recording } from '../types';
import { getCase } from '../hooks/cases';
import { apiFetch } from '../utils/api';
import { uploadAudio } from '../utils/uploadAudio';
import { toast } from 'sonner';
import BrowserLocalRecordingRecoveryPrompt from './BrowserLocalRecordingRecoveryPrompt';
import { useBrowserLocalRecordingRecoveryPrompt } from '../hooks/useBrowserLocalRecordingRecoveryPrompt';
import { defaultBrowserLocalRecordingRecovery } from '../utils/browserLocalRecordingRecovery';

interface TranscriptStatusChange {
  isProcessing: boolean;
  hasText: boolean;
  hasError?: boolean;
}

interface AdditionalRecordingCardProps {
  conversationId: string;
  onTranscriptionComplete: (workflowId: string) => void;
  onRecordingStarted: () => void;
  onTranscriptionStatusChange?: (status: TranscriptStatusChange) => void;
  replaceExisting?: boolean;
  onIgnoreFailedVoiceOver?: () => void;
}

const AdditionalRecordingCard: React.FC<AdditionalRecordingCardProps> = ({
  conversationId,
  onTranscriptionComplete,
  onRecordingStarted,
  onTranscriptionStatusChange,
  replaceExisting = false,
  onIgnoreFailedVoiceOver,
}) => {
  // All hooks must be declared at the top, before any conditional returns
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [continuationSeedBlob, setContinuationSeedBlob] = useState<Blob | null>(null);
  const [continuationSeedDurationMs, setContinuationSeedDurationMs] = useState<number | undefined>(undefined);
  const [recoveredPlaybackBlob, setRecoveredPlaybackBlob] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcript, setTranscript] = useState<string | null>(null);
  const [anonymizedTranscript, setAnonymizedTranscript] = useState<string | null>(null);
  const [showAnonymized, setShowAnonymized] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState<string | null>(null);
  const [uploadedRecording, setUploadedRecording] = useState<Recording | null>(null);
  const {
    recoverableRecording,
    isInspectingRecovery,
    recoveryWarning,
    restoreRecoveredRecording,
    discardRecoveredRecording,
  } = useBrowserLocalRecordingRecoveryPrompt({
    conversationId,
    recordingType: 'voice-over',
    unavailableMessage: 'Vorige persoonlijke opname kon niet worden hersteld. Je kunt een nieuwe opname starten.',
    inspectErrorMessage: 'Could not inspect browser-local voice-over recovery:',
  });

  // Upload audio and start transcription
  const handleRecordingComplete = async (blob: Blob) => {
    setAudioBlob(blob);
    setIsUploading(true);
    onTranscriptionStatusChange?.({ isProcessing: true, hasText: false });

    try {
      const workflow_id = await uploadAudio({
        blob,
        conversationId,
        recordingType: 'voice-over',
      });
      await defaultBrowserLocalRecordingRecovery.discard(conversationId, 'voice-over');
      setContinuationSeedBlob(null);
      setRecoveredPlaybackBlob(null);

      const refreshedCase = await getCase(conversationId);
      setUploadedRecording(refreshedCase.voice_over ?? null);

      setIsUploading(false);
      setIsTranscribing(true);

      pollTranscription(workflow_id);

    } catch (error) {
      console.error('Error uploading audio:', error);
      setIsUploading(false);
      onTranscriptionStatusChange?.({ isProcessing: false, hasText: false, hasError: true });
      toast.error("Fout bij uploaden audio", {
        description: error instanceof Error ? error.message : 'Onbekende fout opgetreden',
        duration: 8000,
      });
    }
  };

  // Poll for transcription result
  const pollTranscription = async (workflow_id: string) => {
    try {
      const response = await apiFetch(`/audio/transcribe/result/${workflow_id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch transcription: ${response.status}`);
      }

      const data = await response.json();

      if (data.text) {
        setTranscript(data.text);
        setAnonymizedTranscript(data.anonymized_text || data.text);
        setIsTranscribing(false);
        onTranscriptionStatusChange?.({ isProcessing: false, hasText: true });
        const refreshedCase = await getCase(conversationId);
        setUploadedRecording(refreshedCase.voice_over ?? null);
        onTranscriptionComplete(workflow_id);
        toast.success("Persoonlijke Advies & Zienswijze verwerkt!", {
          description: "Je aanvullende opname is succesvol verwerkt",
          duration: 3000,
        });
      } else if (data.error_message && data.error_message !== "Workflow not completed") {
        setTranscriptionError(data.error_message);
        setIsTranscribing(false);
        onTranscriptionStatusChange?.({ isProcessing: false, hasText: false, hasError: true });
        toast.error("Verwerking mislukt", {
          description: data.error_message,
          duration: 8000,
        });
      } else {
        // Still processing, poll again
        setTimeout(() => pollTranscription(workflow_id), 5000);
      }
    } catch (error) {
      console.error('Error polling transcription:', error);
      setTranscriptionError(error instanceof Error ? error.message : 'Onbekende fout');
      setIsTranscribing(false);
      onTranscriptionStatusChange?.({ isProcessing: false, hasText: false, hasError: true });
      toast.error("Fout bij ophalen resultaat", {
        description: error instanceof Error ? error.message : 'Onbekende fout',
        duration: 8000,
      });
    }
  };

  const handleIgnoreFailedVoiceOver = async () => {
    const response = await apiFetch(
      `/conversation/${conversationId}/recordings/voice-over/ignore-failed`,
      { method: 'POST' }
    );
    if (!response.ok) {
      throw new Error(`Ignore failed voice-over failed: ${response.status}`);
    }
    setUploadedRecording(null);
    setTranscriptionError(null);
    onTranscriptionStatusChange?.({ isProcessing: false, hasText: false });
    onIgnoreFailedVoiceOver?.();
  };

  const handleRetranscriptionStarted = (workflowId: string) => {
    setTranscriptionError(null);
    setIsTranscribing(true);
    onTranscriptionStatusChange?.({ isProcessing: true, hasText: false });
    pollTranscription(workflowId);
  };

  // Handle recording reset
  const handleRecordingReset = () => {
    setAudioBlob(null);
    setContinuationSeedBlob(null);
    setRecoveredPlaybackBlob(null);
    setTranscript(null);
    setAnonymizedTranscript(null);
    setTranscriptionError(null);
    setUploadedRecording(null);
    setIsTranscribing(false);
    onTranscriptionStatusChange?.({ isProcessing: false, hasText: false });
  };

  const handleRestoreRecoveredRecording = () => {
    const restoredRecording = restoreRecoveredRecording();
    if (!restoredRecording) return;

    setAudioBlob(restoredRecording.blob);
    setRecoveredPlaybackBlob(restoredRecording.blob);
  };

  const handleContinueRecoveredRecording = async () => {
    const restoredRecording = restoreRecoveredRecording();
    if (!restoredRecording) return;

    setContinuationSeedBlob(restoredRecording.blob);
    setContinuationSeedDurationMs(restoredRecording.metadata.durationMs);
    setRecoveredPlaybackBlob(restoredRecording.blob);
    setAudioBlob(null);
    // Keep the recovered session in storage: the continuation is recorded as a
    // new session and the two are joined when the recording finishes.
  };

  const handleFreshRecordingStart = async () => {
    // When continuing a recovered take, keep the recovered session in storage;
    // only a genuinely fresh recording should clear earlier recovery slots.
    if (!continuationSeedBlob) {
      await defaultBrowserLocalRecordingRecovery.prepareForFreshRecording(conversationId, 'voice-over');
    }
    onRecordingStarted();
  };

  const handleDiscardRecoveredRecording = async () => {
    await discardRecoveredRecording();
    setAudioBlob(null);
    setContinuationSeedBlob(null);
    setRecoveredPlaybackBlob(null);
  };

  // Same reason as the main recorder: showing the recorder first and swapping
  // to the recovery options a moment later reads as a glitch.
  if (isInspectingRecovery) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8">
        <p className="text-base text-brand-blauw" role="status">
          Controleren op een eerdere opname…
        </p>
      </div>
    );
  }

  if (recoverableRecording) {
    return (
      <BrowserLocalRecordingRecoveryPrompt
        title="De opname van je persoonlijke opmerkingen is gepauzeerd."
        description="Deze opname is eerder gestart en actief gepauzeerd of door een probleem met internet afgebroken."
        durationMs={recoverableRecording.metadata.durationMs}
        onRestore={handleRestoreRecoveredRecording}
        onContinue={handleContinueRecoveredRecording}
        onDiscard={handleDiscardRecoveredRecording}
        className="bg-white border border-amber-300 rounded-lg p-8"
        headingLevel={2}
        titleClassName="text-2xl font-semibold text-brand-red mb-2"
        descriptionClassName="text-brand-blauw text-sm mb-6"
      />
    );
  }

  // Show processing state (similar to TaskDisplay)
  if (isUploading || isTranscribing) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        <div className="text-left">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-8 border-4 border-brand-blauw border-t-transparent rounded-full animate-spin"></div>
            <h3 className="text-2xl font-semibold text-brand-red">Persoonlijke Advies & Zienswijze</h3>
          </div>
          <p className="text-brand-blauw text-sm">
            Je opmerkingen worden verwerkt, dit kan even duren.
          </p>
        </div>
        <RecordingAudioPlayback
          conversationId={conversationId}
          recording={uploadedRecording}
          label="Persoonlijke opmerkingen"
        />
      </div>
    );
  }

  if (transcriptionError && uploadedRecording) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        <div className="text-left mb-6">
          <h2 className="text-2xl font-semibold text-brand-red mb-2">
            Persoonlijke opmerkingen
          </h2>
          <p className="text-red-600 text-sm">
            ❌ Je persoonlijke opmerkingen verwerking is mislukt.
          </p>
        </div>

        <RecordingAudioPlayback
          conversationId={conversationId}
          recording={uploadedRecording}
          label="Persoonlijke opmerkingen"
        />

        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 text-sm">{transcriptionError}</p>
        </div>

        <div className="mt-4">
          <RecordingRetranscriptionAction
            conversationId={conversationId}
            recording={uploadedRecording}
            onRetranscriptionStarted={handleRetranscriptionStarted}
          />
        </div>
        <button
          type="button"
          onClick={handleIgnoreFailedVoiceOver}
          className="mt-4 text-sm text-brand-blauw hover:underline"
        >
          Doorgaan zonder persoonlijke opmerkingen
        </button>
      </div>
    );
  }

  // Show completed transcription (similar to TaskDisplay)
  if (transcript && !isTranscribing) {
    return (
      <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
        <div className="text-left mb-6">
          <h2 className="text-2xl font-semibold text-brand-red mb-2">
            Persoonlijke opmerkingen
          </h2>
          <p className="text-brand-blauw text-sm">
            Je persoonlijke opmerkingen zijn succesvol verwerkt.
          </p>
        </div>

        <RecordingAudioPlayback
          conversationId={conversationId}
          recording={uploadedRecording}
          label="Persoonlijke opmerkingen"
        />

        {/* Anonymization Toggle */}
        {anonymizedTranscript && (
          <div className="flex items-center gap-2 mb-4">
            {/* <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAnonymized}
                onChange={(e) => setShowAnonymized(e.target.checked)}
                className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 focus:ring-2"
              />
              <span className="text-sm text-gray-700">Gebruik geanonimiseerde tekst</span>
            </label> */}
          </div>
        )}
        
        {/* Transcription Text */}
        <div className="bg-white border border-[#E1D6C6] rounded-xl p-6 max-h-96 overflow-y-auto">
          <p className="text-brand-black text-sm leading-relaxed whitespace-pre-wrap">
            {showAnonymized && anonymizedTranscript ? anonymizedTranscript : transcript}
          </p>
        </div>
      </div>
    );
  }

  // Show recorder (initial state or when recording)
  return (
    <div className="bg-white border border-[#E1D6C6] rounded-xl p-8 shadow-sm">
      {recoveryWarning && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" role="status">
          {recoveryWarning}
        </div>
      )}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-brand-red mb-2">
          {replaceExisting ? 'Nieuwe persoonlijke opmerkingen opnemen' : 'Advies en zienswijze toevoegen?'}
        </h2>
        <p className="text-brand-blauw text-sm">
          Voeg je professionele zienswijze, conclusie & advies toe.
        </p>
      </div>
      <BrowserLocalRecordingPlayback
        blob={recoveredPlaybackBlob}
        label="Herstelde persoonlijke opname"
      />
      <AudioRecorder
        conversationId={conversationId}
        onRecordingComplete={handleRecordingComplete}
        onReset={handleRecordingReset}
        onSubmit={() => {
          if (audioBlob) {
            handleRecordingComplete(audioBlob);
          }
        }} 
        audioBlob={audioBlob}
        isSubmitting={isUploading}
        isTranscribing={isTranscribing}
        transcript={transcript}
        anonymizedTranscript={anonymizedTranscript}
        showAnonymized={showAnonymized}
        onToggleAnonymized={setShowAnonymized}
        transcriptionError={transcriptionError}
        autoStart={Boolean(continuationSeedBlob)}
        onRecordingStart={handleFreshRecordingStart}
        setHeaderMessage={() => {}}
        recordingType="voice-over"
        leadingChunkBlob={continuationSeedBlob}
        leadingDurationMs={continuationSeedDurationMs}
      />
    </div>
  );
};

export default AdditionalRecordingCard;

